import { create } from 'zustand';

import type {
  AuditEntry,
  ColumnPolicyOverride,
  FiaArtifact,
  HitlItem,
  Manifest,
  RasSchemaResponse,
  RunStatus,
  SchemaDriftReport,
} from '../services/types';

/**
 * Global app state.
 *
 * Keep this *small*. Per-page transient state stays local (useState); only
 * truly app-wide concerns (wake-up status, current run id, reviewer session)
 * live here.
 *
 * Stage 2 additions
 * -----------------
 * - A `runs` map caches the four artifacts we fetch per run (FIA, schema,
 *   manifest, HITL list) so tab switches are instant.
 * - A `drafts` map holds the reviewer's unsaved schema/manifest edits; they
 *   only flow back to the server via explicit "Save" actions.
 */

export interface WakeState {
  status: 'idle' | 'probing' | 'awake' | 'failed';
  attempts: number;
  error?: string;
}

export interface RunCache {
  runId: string;
  status?: RunStatus;
  /** Only populated when `status === "error"` — truncated traceback from the backend. */
  error_reason?: string;
  /** MOA executor status — `"partial"` when one or more cleansers failed but the pipeline completed. */
  run_result_status?: string;
  /** Cleanser anomaly count. */
  anomaly_count?: number;
  /** Schema drift report from core.schema_guard — undefined in v0.1 (no persistent baseline). */
  schema_drift?: SchemaDriftReport | null;
  /**
   * v0.4 Stage D — Cross-field validator output count. ``undefined``
   * means no rules applied to this run; ``0`` means rules ran with
   * no violations; ``> 0`` means violations to review.
   */
  cross_field_flag_count?: number | null;
  /**
   * v0.5 Stage E.4 — reviewer-set per-column policy overrides
   * currently in force for this run. ``null`` / absent on runs
   * without any overrides. SchemaReview hydrates its threshold
   * inputs from this map.
   */
  column_policy_overrides?: Record<string, ColumnPolicyOverride> | null;
  /**
   * v0.6 Stage B6 — column names matched by the post-FIA header PII
   * keyword scan. Drives the upload-preview blur immediately on the
   * first render (no plain-text PII pre-HITL). Union'd with
   * ``schema.pii_columns`` once RAS populates them.
   */
  header_pii_columns?: string[] | null;
  fia?: FiaArtifact;
  schema?: RasSchemaResponse;
  manifest?: Manifest;
  hits?: HitlItem[];
  /** Most-recent progress percent from the WS stream (0–100). */
  progressPct?: number;
  /**
   * Per-artifact fetch errors. Stored here (not in component state) so they
   * survive the React StrictMode mount-unmount-remount cycle and the
   * auto-fetch guard in useRun doesn't re-fire after an error.
   */
  fetchErrors?: Partial<Record<'fia' | 'schema' | 'manifest' | 'hits', string>>;
  /**
   * HTTP status code for each per-artifact error, where available. Paired
   * with `fetchErrors` so views can branch on status (e.g. render a soft
   * empty-state for 404 "not yet available" instead of the red error block).
   * `undefined` for non-ApiError exceptions (network errors, etc.).
   */
  fetchErrorStatuses?: Partial<Record<'fia' | 'schema' | 'manifest' | 'hits', number>>;
}

export interface RunDrafts {
  schema?: RasSchemaResponse;
  manifest?: Manifest;
}

interface AppState {
  wakeState: WakeState;
  setWakeState: (s: WakeState) => void;

  currentRunId: string | null;
  setCurrentRunId: (id: string | null) => void;

  runs: Record<string, RunCache>;
  updateRun: (runId: string, patch: Partial<RunCache>) => void;
  clearRun: (runId: string) => void;

  drafts: Record<string, RunDrafts>;
  setDraft: <K extends keyof RunDrafts>(runId: string, key: K, value: RunDrafts[K]) => void;
  clearDraft: (runId: string, key: keyof RunDrafts) => void;

  // --- Stage 3: cross-run HITL dashboard ------------------------------------
  /**
   * Flat cross-run index of hit_id → HitlItem. The run-scoped caches under
   * `runs[id].hits` are still used for the in-tab queue; this flat map exists
   * so the /hitl dashboard can render everything in one list without
   * reshuffling run caches on each filter change. Updates to a hit should
   * flow through both caches via `upsertHit`.
   */
  hitsIndex: Record<string, HitlItem>;
  upsertHits: (items: HitlItem[]) => void;
  /**
   * v0.6 Stage 1 (H3) — replace all hitsIndex entries for a given run_id
   * with the supplied list. Use after a run-scoped refetch so resolved
   * hits from a previous fetch don't linger in the cross-run cache.
   * Cross-run callers (the dashboard) keep using `upsertHits` since they
   * have no single run_id to scope by.
   */
  replaceHits: (runId: string, items: HitlItem[]) => void;
  updateHit: (hitId: string, patch: Partial<HitlItem>) => void;

  /** Per-hit audit trail cache. Populated by ReviewerModal on open. */
  auditByHit: Record<string, AuditEntry[]>;
  setAuditTrail: (hitId: string, entries: AuditEntry[]) => void;
  appendAudit: (hitId: string, entry: AuditEntry) => void;

  /**
   * Monotonically increasing counter bumped whenever a `hitl_created` WS
   * event fires. The dashboard subscribes and re-queries when it changes, so
   * new hits appear without a manual refresh.
   */
  hitlVersion: number;
  bumpHitlVersion: () => void;
}

export const useAppStore = create<AppState>((set) => ({
  wakeState: { status: 'idle', attempts: 0 },
  setWakeState: (wakeState) => set({ wakeState }),

  currentRunId: null,
  setCurrentRunId: (currentRunId) => set({ currentRunId }),

  runs: {},
  updateRun: (runId, patch) =>
    set((state) => ({
      runs: {
        ...state.runs,
        [runId]: { ...(state.runs[runId] ?? { runId }), ...patch, runId },
      },
    })),
  clearRun: (runId) =>
    set((state) => {
      const { [runId]: _drop, ...rest } = state.runs;
      void _drop;
      return { runs: rest };
    }),

  drafts: {},
  setDraft: (runId, key, value) =>
    set((state) => ({
      drafts: {
        ...state.drafts,
        [runId]: { ...(state.drafts[runId] ?? {}), [key]: value },
      },
    })),
  clearDraft: (runId, key) =>
    set((state) => {
      const current = state.drafts[runId] ?? {};
      const { [key]: _drop, ...rest } = current;
      void _drop;
      return { drafts: { ...state.drafts, [runId]: rest } };
    }),

  hitsIndex: {},
  upsertHits: (items) =>
    set((state) => {
      const next = { ...state.hitsIndex };
      for (const h of items) next[h.hit_id] = h;
      return { hitsIndex: next };
    }),
  replaceHits: (runId, items) =>
    set((state) => {
      // Drop every hitsIndex entry tied to this run before re-inserting
      // the fresh list. Without this, a hit that was resolved on the
      // backend (and therefore omitted from the H1-default-open response)
      // would survive as a stale entry from the previous fetch.
      const next: Record<string, HitlItem> = {};
      for (const [hitId, hit] of Object.entries(state.hitsIndex)) {
        if (hit.run_id !== runId) next[hitId] = hit;
      }
      for (const h of items) next[h.hit_id] = h;
      return { hitsIndex: next };
    }),
  updateHit: (hitId, patch) =>
    set((state) => {
      const existing = state.hitsIndex[hitId];
      if (!existing) return {};
      const merged = { ...existing, ...patch };
      // Keep the run-scoped cache in sync so the per-run tab stays accurate.
      const runId = existing.run_id;
      const runCache = state.runs[runId];
      const nextRuns =
        runCache?.hits
          ? {
              ...state.runs,
              [runId]: {
                ...runCache,
                hits: runCache.hits.map((h) => (h.hit_id === hitId ? merged : h)),
              },
            }
          : state.runs;
      return {
        hitsIndex: { ...state.hitsIndex, [hitId]: merged },
        runs: nextRuns,
      };
    }),

  auditByHit: {},
  setAuditTrail: (hitId, entries) =>
    set((state) => ({
      auditByHit: { ...state.auditByHit, [hitId]: entries },
    })),
  appendAudit: (hitId, entry) =>
    set((state) => ({
      auditByHit: {
        ...state.auditByHit,
        [hitId]: [...(state.auditByHit[hitId] ?? []), entry],
      },
    })),

  hitlVersion: 0,
  bumpHitlVersion: () => set((state) => ({ hitlVersion: state.hitlVersion + 1 })),
}));
